import { Icon, type IconName } from '@thetreep/travel-ui';
import {
	Button,
	Text,
	UNSTABLE_Toast as Toast,
	UNSTABLE_ToastContent as ToastContent,
	UNSTABLE_ToastRegion as ToastRegionPrimitive,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';

import type { ToastContent as ToastData } from '@/lib/toast';
import { toastQueue } from '@/lib/toast';

const toastClassName = tv({
	base: 'relative w-[424px] rounded-lg border p-4 shadow-[0px_8px_12px_-4px_rgba(0,0,17,0.1)]',
	variants: {
		type: {
			error: 'border-semantic-red-error-700 bg-semantic-red-error-100',
			success:
				'border-semantic-green-success-700 bg-semantic-green-success-100',
			info: 'border-semantic-blue-info-700 bg-semantic-blue-info-100',
		},
	},
});

const contentClassName = tv({
	base: 'flex gap-4 typography-text-m font-bold',
	variants: {
		type: {
			error: 'text-semantic-red-error-700',
			success: 'text-semantic-green-success-800',
			info: 'text-blue-800',
		},
	},
});

const typeToIcon: Record<ToastData['type'], IconName> = {
	error: 'indicator-warning',
	success: 'indicator-eligibility-true',
	info: 'info-fill',
};

export function ToastRegion() {
	const { t } = useTranslation();
	return (
		<ToastRegionPrimitive
			queue={toastQueue}
			className="fixed top-4 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2 outline-none"
		>
			{({ toast }) => (
				<Toast
					toast={toast}
					className={toastClassName({ type: toast.content.type })}
				>
					<ToastContent
						className={contentClassName({ type: toast.content.type })}
					>
						<Icon
							name={typeToIcon[toast.content.type]}
							width={20}
							height={20}
						/>
						<div className="flex flex-col gap-0.5">
							<Text slot="title">{toast.content.title}</Text>
							{toast.content.description && (
								<Text
									slot="description"
									className="block typography-text-m font-medium text-neutral-gray-800"
								>
									{toast.content.description}
								</Text>
							)}
							{toast.content.reference && (
								<Text className="block typography-text-s font-medium text-neutral-gray-800">
									{t('common.api.referenceLabel')}:{' '}
									<span className="select-all">{toast.content.reference}</span>
								</Text>
							)}
						</div>
					</ToastContent>
					<Button
						slot="close"
						aria-label={t('common.close')}
						className="absolute top-4 right-4 cursor-pointer text-neutral-gray-800 opacity-70 hover:opacity-100"
						onPress={() => toastQueue.close(toast.key)}
					>
						<Icon name="close" width={16} height={16} />
					</Button>
				</Toast>
			)}
		</ToastRegionPrimitive>
	);
}
